import React, { useState } from 'react';
import { Sparkles, UserRound } from 'lucide-react';
import { AdminAIComparisonModal, AdminAIThumbnailGallery } from '../admin/AdminAIImageComponents';
import {
  Card,
  SectionCard,
  EmptyState,
  LoadingState,
  PaginationControls,
  formatDateTime,
  resolveMediaSrc,
  formatNumber,
  StatusBadge,
} from './SuperAdminUI';

function normalizeOriginalImage(item) {
  return resolveMediaSrc(
    item?.scan?.image_url ||
      item?.scan?.original_image ||
      item?.image_url ||
      item?.original_image ||
      item?.images?.original?.legacy_url ||
      item?.images?.original?.path
  );
}

function normalizeGeneratedImages(item) {
  const generated = item?.scan?.generated_images || item?.generated_images || item?.images?.generated || [];
  const normalized = Array.isArray(generated) ? generated : [];
  const mapped = normalized
    .map((image, index) => {
      if (typeof image === 'string') {
        return {
          id: `generated-${index}`,
          src: resolveMediaSrc(image),
          timestamp: null,
          raw: image,
        };
      }

      return {
        id: image?.id || `generated-${index}`,
        path: resolveMediaSrc(image?.url || image?.path || image?.image_url || image?.image_path),
        src: resolveMediaSrc(image?.url || image?.path || image?.image_url || image?.image_path),
        timestamp: image?.timestamp || image?.generated_at || image?.created_at || image?.metadata?.timestamp || null,
        generatedAt: image?.timestamp || image?.generated_at || image?.created_at || image?.metadata?.timestamp || null,
        provider: image?.provider || item?.provider || item?.scan?.provider || item?.metadata?.provider || null,
        faceShape: image?.face_shape || image?.faceShape || item?.face_shape || null,
        haircut: image?.haircut || item?.haircut || null,
        hair_color: image?.hair_color || item?.hair_color || null,
        raw: image,
      };
    })
    .filter((image) => image.src);

  if (!mapped.length && item?.images?.legacy_result_url) {
    return [
      {
        id: 'legacy-result',
        path: resolveMediaSrc(item.images.legacy_result_url),
        src: resolveMediaSrc(item.images.legacy_result_url),
        timestamp: item?.timestamps?.generated?.[0] || null,
        generatedAt: item?.timestamps?.generated?.[0] || null,
        provider: item?.provider || item?.scan?.provider || item?.metadata?.provider || null,
        faceShape: item?.face_shape || null,
        haircut: item?.haircut || null,
        hair_color: item?.hair_color || null,
        raw: item.images.legacy_result_url,
      },
    ];
  }

  return mapped;
}

function resolvePrompt(item) {
  return (
    item?.scan?.prompt ||
    item?.metadata?.prompt ||
    item?.scan?.metadata?.prompt ||
    item?.scan?.hairstyle_suggestions?.promptUsed ||
    item?.scan?.hairstyle_suggestions?.prompt ||
    item?.prompt ||
    ''
  );
}

const SuperAdminAIViewer = ({ items = [], shopName, loading = false, pagination = null, onPageChange }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeScan, setActiveScan] = useState(null);

  const openViewer = (item, image, type) => {
    setActiveScan({
      originalImage: normalizeOriginalImage(item),
      generatedImages: normalizeGeneratedImages(item),
      resultUrl: item?.images?.legacy_result_url || null,
      client: item?.client || null,
      client_name: item?.client?.name || null,
      face_shape: item?.face_shape || null,
      haircut: item?.haircut || null,
      hair_color: item?.hair_color || null,
      provider: item?.provider || null,
      credits_used: item?.credits_used || null,
      generation_status: item?.generation_status || null,
      prompt: resolvePrompt(item),
      timestamps: item?.timestamps || {},
      created_at: item?.timestamps?.scan_created_at || null,
    });
    setSelectedImage({ ...image, item, type });
  };

  return (
    <>
      <SectionCard
        title="AI Images"
        subtitle={shopName ? `Original and generated imagery linked to ${shopName}` : 'Original and generated imagery linked to this shop'}
        icon={<Sparkles className="h-5 w-5" style={{ color: '#7d6633' }} />}
      >
        {loading ? (
          <LoadingState lines={6} />
        ) : !items.length ? (
          <EmptyState
            title="No AI images found"
            description="This shop does not have any stored scan results for the current query."
            icon={<Sparkles className="h-6 w-6 text-zinc-400" />}
          />
        ) : (
          <div className="space-y-5">
            {items.map((item) => {
              const originalSrc = normalizeOriginalImage(item);
              const generatedImages = normalizeGeneratedImages(item);

              return (
                <Card key={item.scan_id} className="overflow-hidden">
                  <div className="border-b border-white/10 p-4 md:p-5">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Scan #{item.scan_id}</h3>
                          <StatusBadge status={item.generation_status || 'unknown'} variant={item.generation_status === 'completed' ? 'success' : item.generation_status === 'failed' ? 'danger' : 'warning'} />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-zinc-600 dark:text-zinc-400">
                          <span className="inline-flex items-center gap-2">
                            <UserRound className="h-4 w-4" />
                            Client: {item.client?.name || 'Unknown'}
                          </span>
                          <span>Barber: {item.barber?.name || 'Unknown'}</span>
                          <span>Face shape: {item.face_shape || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="grid gap-1 text-xs text-zinc-500 dark:text-zinc-400 sm:grid-cols-2 lg:grid-cols-1">
                        <div>Credits used: {formatNumber(item.credits_used)}</div>
                        <div>Created: {formatDateTime(item.timestamps?.scan_created_at)}</div>
                        <div>Generated count: {generatedImages.length}</div>
                      </div>
                    </div>
                  </div>

                  <AdminAIThumbnailGallery
                    originalImage={originalSrc}
                    generatedImages={generatedImages}
                    showGeneratedEmptyState
                    onOriginalClick={() =>
                      openViewer(item, { path: originalSrc, src: originalSrc, timestamp: item.timestamps?.scan_created_at }, 'original')
                    }
                    onGeneratedClick={(image) => openViewer(item, image, 'generated')}
                  />
                </Card>
              );
            })}

            {pagination && onPageChange ? (
              <PaginationControls pagination={pagination} onPageChange={onPageChange} loading={loading} />
            ) : null}
          </div>
        )}
      </SectionCard>

      <AdminAIComparisonModal
        isOpen={Boolean(selectedImage)}
        onClose={() => {
          setSelectedImage(null);
          setActiveScan(null);
        }}
        activeScan={activeScan}
        selectedImage={selectedImage}
        clientName={selectedImage?.item?.client?.name || 'Unknown client'}
        showProvider={false}
      />
    </>
  );
};

export default SuperAdminAIViewer;
